// Admin-only floating panel. Mount conditionally based on the caller's role
// check. Each control wires through useAdminTweaks (optimistic + debounced PUT).

import { useAdminTweaks } from '@/marketing/queries/useTweaks';
import { PALETTES } from '@/design-system/palettes';
import type { PaletteKey } from '@/shared/tweaks-defaults';
import {
  TweakWrapper,
  TweakSection,
  TweakToggle,
  TweakRadio,
  TweakSelect,
  TweakSlider,
  TweakColor,
} from './TweakControls';

const PALETTE_KEYS: PaletteKey[] = ['globe', 'passport', 'departures', 'coastal'];
const PALETTE_SWATCHES = PALETTE_KEYS.map((k) => PALETTES[k].swatch) as Array<
  [string, string, string]
>;

export function TweaksPanel() {
  const { tweaks, setTweak, isSaving, lastSavedAt, error } = useAdminTweaks();
  const paletteIdx = PALETTE_KEYS.indexOf(tweaks.palette);

  return (
    <TweakWrapper title="Tweaks">
      <p
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          letterSpacing: '.08em',
          color: error ? 'var(--coral-2)' : 'var(--muted)',
          marginBottom: 12,
        }}
      >
        {error
          ? `Save failed: ${error.message}`
          : isSaving
            ? 'Saving…'
            : lastSavedAt
              ? `Saved · ${new Date(lastSavedAt).toLocaleTimeString()}`
              : 'Live'}
      </p>

      <TweakSection label="Look" />
      <TweakColor
        label="Palette"
        swatches={PALETTE_SWATCHES}
        selectedIdx={paletteIdx === -1 ? 0 : paletteIdx}
        onChange={(i) => setTweak('palette', PALETTE_KEYS[i])}
      />
      <TweakRadio<'serif' | 'sans'>
        label="Display font"
        value={tweaks.displayFont}
        options={['serif', 'sans']}
        onChange={(v) => setTweak('displayFont', v)}
      />
      <TweakRadio<'compact' | 'regular' | 'airy'>
        label="Density"
        value={tweaks.density}
        options={['compact', 'regular', 'airy']}
        onChange={(v) => setTweak('density', v)}
      />
      <TweakToggle label="Dark mode" value={tweaks.dark} onChange={(v) => setTweak('dark', v)} />
      <TweakSlider
        label="Route motif"
        value={tweaks.routeIntensity}
        min={0}
        max={0.8}
        step={0.05}
        onChange={(v) => setTweak('routeIntensity', v)}
      />

      <TweakSection label="Hero" />
      <TweakSelect<'default' | 'bolder' | 'quiet' | 'business'>
        label="Hero copy"
        value={tweaks.heroVariant}
        options={[
          { value: 'default', label: 'Default' },
          { value: 'bolder', label: 'Bolder' },
          { value: 'quiet', label: 'Quiet' },
          { value: 'business', label: 'Business' },
        ]}
        onChange={(v) => setTweak('heroVariant', v)}
      />
      <TweakRadio<'split' | 'video' | 'static'>
        label="Hero layout"
        value={tweaks.heroLayout}
        options={['split', 'video', 'static']}
        onChange={(v) => setTweak('heroLayout', v)}
      />
      <TweakSelect<'default' | 'travel' | 'direct' | 'outcome'>
        label="CTA copy"
        value={tweaks.ctaCopy}
        options={[
          { value: 'default', label: 'Start free today' },
          { value: 'travel', label: 'Start with free basic packs' },
          { value: 'direct', label: 'Try it free' },
          { value: 'outcome', label: 'Handle your next trip' },
        ]}
        onChange={(v) => setTweak('ctaCopy', v)}
      />

      <TweakSection label="Layout" />
      <TweakRadio<'timeline' | 'grid' | 'accordion'>
        label="Personas"
        value={tweaks.personaLayout}
        options={['timeline', 'grid', 'accordion']}
        onChange={(v) => setTweak('personaLayout', v)}
      />
      <TweakRadio<'cards' | 'table' | 'toggle'>
        label="Pricing"
        value={tweaks.pricingLayout}
        options={['cards', 'table', 'toggle']}
        onChange={(v) => setTweak('pricingLayout', v)}
      />
      <TweakToggle
        label="Waveforms"
        value={tweaks.showWaveforms}
        onChange={(v) => setTweak('showWaveforms', v)}
      />

      <TweakSection label="Sections" />
      <TweakToggle
        label="Trust strip"
        value={tweaks.showTrustStrip}
        onChange={(v) => setTweak('showTrustStrip', v)}
      />
      <TweakToggle
        label="Comparison"
        value={tweaks.showComparison}
        onChange={(v) => setTweak('showComparison', v)}
      />
      <TweakToggle
        label="Mini demos"
        value={tweaks.showMiniDemos}
        onChange={(v) => setTweak('showMiniDemos', v)}
      />
      <TweakToggle
        label="Founder"
        value={tweaks.showFounder}
        onChange={(v) => setTweak('showFounder', v)}
      />
    </TweakWrapper>
  );
}
